/* Copyright (c) 2020-present CloudIO Inc */

import { CommonProps } from '../layout/Sidebar/panels/PropertiesView/CommonProperties';
import { getManager } from '../registry/Manager';
import { Platform } from '../platform/Platform';
import {
  ComponentSample,
  IconValue,
  PropertyDefinitions,
  TriggerManager,
  TriggerManagerFn,
} from '../registry/Component.type';
import {
  ActionItemType,
  ItemType,
  TriggerItemType,
  TriggerItemTypeRegistry,
} from '../registry/ComponentRegistry';
import { baseTriggerManager } from '../registry/Components';
import { triggerDefaultValue } from './Triggers';
import { analyzeTrigger } from './TriggerAnalyze';
import { responsiveTriggersHelpRenders } from './TriggerOnWindowSizeChange_Help';

export const triggerIcons: Record<string, IconValue> = {
  TriggerOnClick: { iconName: 'hand-pointer' },
  TriggerOnExtraLargeScreen: { iconName: 'desktop' },
  TriggerOnLargeScreen: { iconName: 'laptop' },
  TriggerOnMediumScreen: { iconName: 'tablet' },
  TriggerOnPageActive: { iconName: 'portal-enter' },
  TriggerOnPageExit: { iconName: 'portal-exit' },
  TriggerOnWindowSizeChange: { iconName: 'expand' },
  TriggerOnLandscapeScreen: { iconName: 'rectangle-landscape' },
  TriggerOnPortraitScreen: { iconName: 'rectangle-portrait' },
  TriggerOnSmallScreen: { iconName: 'mobile-notch' },
  TriggerOnExtraSmallScreen: { iconName: 'mobile-screen-button' },
  TriggerAfterSave: { iconName: 'save' },
  TriggerBeforeSave: { iconName: 'save' },
  TriggerBeforeCreate: { iconName: 'plus' },
  TriggerAfterQuery: { iconName: 'redo' },
  TriggerBeforeQuery: { iconName: 'undo' },
};

export const triggerNames: Record<TriggerItemType, string> = {
  TriggerAfterServerValidate: 'Trigger After Server Validate',
  TriggerAfterUpload: 'Trigger After Upload',
  TriggerAfterPaste: 'Trigger After Paste',
  TriggerOnPaste: 'Trigger On Paste',
  TriggerOnDeleteKeyPress: 'Trigger On Delete Key Press',
  TriggerOnEnterKeyPress: 'Trigger On Enter Key Press',
  TriggerOnRecordActive: 'Trigger On Record Active',
  TriggerAfterDeleteRecord: 'Trigger After Delete Record',
  TriggerBeforeDeleteRecord: 'Trigger Before Delete Record',
  TriggerAfterInsertStoreRecord: 'Trigger After Insert Store Record',
  TriggerBeforeInsertStoreRecord: 'Trigger Before Insert Store Record',
  TriggerOnIconClick: 'Trigger On Icon Click',
  TriggerOnTreeColumnExpand: 'Trigger On Tree Column Expand',
  TriggerOnMention: 'Trigger On Mention',
  TriggerAfterLoad: 'Trigger After Load',
  TriggerAfterQuery: 'Trigger After Query',
  TriggerAfterSave: 'Trigger After Save',
  TriggerBeforeCreate: 'Trigger Before Create',
  TriggerBeforeLoad: 'Trigger Before Load',
  TriggerBeforeQuery: 'Trigger Before Query',
  TriggerBeforeSave: 'Trigger Before Save',
  TriggerOnAggregateClick: 'Trigger On Aggregate Click',
  TriggerOnBlur: 'Trigger On Blur',
  TriggerOnCellClick: 'Trigger On Cell Click',
  TriggerOnChange: 'Trigger On Change',
  TriggerOnClick: 'Trigger On Click',
  TriggerOnComment: 'Trigger On Comment',
  TriggerOnCreate: 'Trigger On Create',
  TriggerOnDelete: 'Trigger On Delete',
  TriggerOnDoubleClick: 'Trigger On Double Click',
  TriggerOnDrop: 'Trigger On Drop',
  TriggerOnDateChange: 'Trigger On Date Change',
  TriggerOnEventChange: 'Trigger On Event Change',
  TriggerOnEventClick: 'Trigger On Event Click',
  TriggerOnEventCreate: 'Trigger On Event Create',
  TriggerOnExtraLargeScreen: 'Trigger On Extra Large Screen',
  TriggerOnExtraSmallScreen: 'Trigger On Extra Small Screen',
  TriggerOnFocus: 'Trigger On Focus',
  TriggerOnInitialize: 'Trigger On Initialize',
  TriggerOnInterval: 'Trigger On Interval',
  TriggerOnKeyDown: 'Trigger On Key Down',
  TriggerOnLandscapeScreen: 'Trigger On Landscape Screen',
  TriggerOnLargeScreen: 'Trigger On Large Screen',
  TriggerOnMediumScreen: 'Trigger On Medium Screen',
  TriggerOnMouseEnter: 'Trigger On Mouse Enter',
  TriggerOnMouseLeave: 'Trigger On Mouse Leave',
  TriggerOnPageActive: 'Trigger On Page Active',
  TriggerOnPageExit: 'Trigger On Page Exit',
  TriggerOnPopoverClose: 'Trigger On Close',
  TriggerOnPopoverOpen: 'Trigger On Open',
  TriggerOnPopupClose: 'Trigger On Close',
  TriggerOnPopupOpen: 'Trigger On Open',
  TriggerOnPortraitScreen: 'Trigger On Portrait Screen',
  TriggerOnResponse: 'Trigger On Response',
  TriggerOnRowClick: 'Trigger On Row Click',
  TriggerOnRowDoubleClick: 'Trigger On Row Double Click',
  TriggerOnSearch: 'Trigger On Search',
  TriggerOnSmallScreen: 'Trigger On Small Screen',
  TriggerOnSubmit: 'Trigger On Submit',
  TriggerOnSubscriptionEvent: 'Trigger On Subscription Event',
  TriggerOnTabActive: 'Trigger On Tab Active',
  TriggerOnTabExit: 'Trigger On Tab Exit',
  TriggerOnValueChange: 'Trigger On Value Change',
  TriggerOnWindowSizeChange: 'Trigger On Window Size Change',
};

export const helpRenders: Record<string, ComponentSample<any>[]> = {
  TriggerOnWindowSizeChange: responsiveTriggersHelpRenders(
    'TriggerOnWindowSizeChange',
  ),
  TriggerOnLargeScreen: responsiveTriggersHelpRenders('TriggerOnLargeScreen'),
  TriggerOnMediumScreen: responsiveTriggersHelpRenders('TriggerOnMediumScreen'),
  TriggerOnSmallScreen: responsiveTriggersHelpRenders('TriggerOnSmallScreen'),
  TriggerOnExtraLargeScreen: responsiveTriggersHelpRenders(
    'TriggerOnExtraLargeScreen',
  ),
  TriggerOnExtraSmallScreen: responsiveTriggersHelpRenders(
    'TriggerOnExtraSmallScreen',
  ),
  TriggerOnPortraitScreen: responsiveTriggersHelpRenders(
    'TriggerOnPortraitScreen',
  ),
  TriggerOnLandscapeScreen: responsiveTriggersHelpRenders(
    'TriggerOnLandscapeScreen',
  ),
};

export const triggerDesc: Record<TriggerItemType, string> = {
  TriggerAfterServerValidate: `**Overview**<br/>
TriggerAfterServerValidate is a server-side validation trigger that executes after the server has completed its validation process. It's designed to perform custom actions or business logic once the server has validated the data but before the final processing or persistence occurs.

**1. Key Characteristics**<br/>
* **Execution Context:** Runs on the server after validation passes.
* **Validation State:** Triggered only when server-side validation is successful.
* **Use Case:** Ideal for implementing complex business rules, data transformations, or additional validations that depend on server-side context.

**2. When to Use**<br/>
* **Post-Validation Processing:** Perform actions that should only occur after successful server validation.
* **Data Enrichment:** Enhance or transform validated data before it's processed further.
* **Cross-Field Validation:** Implement complex validation rules that involve multiple fields.
* **Audit Logging:** Record validation events in an audit trail.
* **Integration Hooks:** Trigger external system notifications or updates after successful validation.

**3. Example Use Cases**<br/>
* **Data Normalization:** Standardize data formats after validation.
* **Derived Fields:** Calculate and set derived field values.
* **Workflow Transitions:** Move a business process to the next state.
* **Notification:** Send confirmation emails or notifications after successful validation.`,

  TriggerAfterUpload: `**Overview**<br/>
TriggerAfterUpload is a server-side trigger that executes after files have been successfully uploaded to the server. It's designed to perform post-upload processing, validation, or integration with other systems once the file transfer is complete.

**1. Key Characteristics**<br/>
* **Execution Context:** Runs on the server after successful file upload
* **Trigger Point:** Activates only after the entire file upload process is complete
* **Use Case:** Ideal for processing uploaded files, triggering workflows, or updating related records`,

  TriggerAfterPaste: `**Overview**<br/>
The Trigger After Paste is an event handler that executes immediately after a user pastes data into a Data Grid component. This trigger allows developers to define logic that processes, validates, or utilizes the newly pasted records once they have been successfully inserted into the grid's local state.

**When to Use**<br/>
Use this trigger when you need to perform actions post-modification. It is distinct from validation triggers in that the data has already been accepted by the grid's input handler. Common use cases include:
* **Post-Processing:** Automatically formatting or calculating derived values based on the pasted rows.
* **Bulk Action Initiation:** Triggering a server-side save or synchronization process immediately after the user pastes a batch of records.
* **User Notification:** Displaying a toast message confirming the number of records pasted or warning if further steps are required.
* **Audit Logging:** Creating an audit trail entry indicating that a bulk paste operation occurred.`,

  TriggerOnPaste: `**Overview**<br/>
The Trigger On Paste is an event handler that executes when values are pasted into a Data Grid component. It is designed to capture the paste event as it occurs, allowing developers to define logic that responds immediately to the introduction of new values via the clipboard.

**When to Use**<br/>
Use this trigger to perform actions specifically tied to the occurrence of a paste event in a data grid. Common use cases include:
* **Immediate Data Handling:** Processing values the moment they are pasted into the grid cells.
* **User Feedback:** Providing immediate visual or auditory cues to the user acknowledging that a paste action has been detected.
* **Workflow Triggering:** Initiating specific UI workflows or property updates that should occur solely because data was entered via paste rather than manual typing.`,

  TriggerOnDeleteKeyPress: `**Overview**<br/>
The Trigger On Delete Key Press is an event handler designed to execute actions specifically when the "Delete" key is pressed. This allows developers to assign custom behaviors to the deletion action within a component context.

**When to Use**<br/>
Use this trigger to perform actions when you press delete. It is particularly useful for:
* **Custom Deletion Logic:** Overriding or augmenting the standard delete behavior with specific business rules.
* **Confirmation Workflows:** Triggering a confirmation dialog or validation check before a deletion occurs.
* **UI Updates:** Modifying other parts of the interface in response to a delete keystroke intention.`,

  TriggerOnEnterKeyPress: `**Overview**<br/>
The Trigger On Enter Key Press is an event handler designed to execute actions specifically when the "Enter" key is pressed. This allows developers to assign custom behaviors to the enter keystroke, often used for submission or confirming an input.

**When to Use**<br/>
Use this trigger to perform actions when you press enter. It is particularly useful for:
* **Form Submission:** Simulating a submit button click when the user presses Enter within a form field [web:6].
* **Search Execution:** Initiating a query or filter action immediately after typing a keyword and hitting Enter [web:4].
* **Navigation Control:** Directing focus or moving to the next step in a workflow [web:1][web:3].
* **Tab Key Handling:** This trigger specifically includes a "Trigger On Tab" property, allowing it to optionally execute when the Tab key is pressed as well.`,

  TriggerOnRecordActive: `
  \`\`\`javascript
// Access the active record information as below using expression
\`\${$._.data.record} \${$._.data.recordId} \${$._.data.rowIndex}\`
\`\`\`
**Overview**<br/>
The Trigger On Record Active is an event handler designed to execute actions specifically when a record becomes active. This is typically used in data-bound components where users interact with specific records, allowing for context-aware logic to run immediately upon selection.

**When to Use**<br/>
Use this trigger to perform actions when a record gets active. It is particularly useful for:
* **Contextual Data Access:** Accessing the active record's information using expressions like \`$._.data.record\`, \`$._.data.recordId\`, and \`$._.data.rowIndex\`.
* **Master-Detail Views:** Updating a detail view or fetching related data when a user selects a row in a master grid.
* **State Updates:** Highlighting or updating UI elements based on the specific data of the active record.`,

  TriggerAfterDeleteRecord: `**Overview**<br/>
The Trigger After Delete Record is an event handler that executes immediately after a deleted record has been successfully posted to the server. It is specifically designed to handle post-deletion logic where the record is confirmed removed from both the backend and the local application state.

**When to Use**<br/>
Use this trigger to perform actions after a deleted record is posted to the server. Key characteristics include:
* **Per-Record Execution:** It is fired individually for each record that got deleted, ensuring that logic can be applied specifically to every item removed during a bulk operation.
* **Post-State Update:** When this trigger fires, the record is already hidden from the UI and removed from the Store, making it suitable for actions that require the "clean" state where the data is no longer present.
* **Synchronization:** Useful for notifying other components or external services that a specific data point has been permanently removed.`,

  TriggerBeforeDeleteRecord: `**Overview**<br/>
The Trigger Before Delete Record is an event handler designed to execute actions immediately before a deleted record is officially posted to the server. While the user interface has already updated to hide the record, this trigger captures the moment prior to the server-side commitment.

**When to Use**<br/>
Use this trigger to perform actions before a deleted record is posted to the server. Key behaviors include:
* **Pre-Commit Execution:** It allows for logic to run locally before the server processes the deletion.
* **Per-Record Processing:** It is fired for each record that is being deleted, allowing for individual handling during batch deletions.
* **UI Feedback State:** When this trigger is fired, the record is already hidden from the UI, meaning the visual removal has occurred even though the backend operation is pending.`,

  TriggerAfterInsertStoreRecord: `**Overview**<br/>
The Trigger After Insert Store Record is an event handler that executes actions immediately after a newly inserted record has been successfully posted to the server. It is designed to handle logic specific to individual record insertions during a save operation.

**When to Use**<br/>
Use this trigger to perform actions after a newly inserted record is posted to the server. Key characteristics include:
* **Per-Record Execution:** It is fired for each record that is being inserted, allowing for granular handling of every new entry.
* **Execution Order:** This trigger is fired before "Trigger After Data Store Save" (TriggerAfterSave), placing it as a specific step within the broader save lifecycle.`,

  TriggerBeforeInsertStoreRecord: `**Overview**<br/>
The Trigger Before Insert Store Record is an event handler that executes actions immediately before a newly inserted record is posted to the server. It allows for specific logic to be applied to individual new records during the save process, occurring after the general save initialization but before the server commitment.

**When to Use**<br/>
Use this trigger to perform actions before a newly inserted record is posted to the server. Key characteristics include:
* **Per-Record Execution:** It is fired for each record that is being inserted, ensuring that validation or modification can happen on a row-by-row basis.
* **Execution Order:** This trigger is fired after "Trigger Before Save" (TriggerBeforeSave), meaning general save logic runs first, followed by this specific insertion logic.`,

  TriggerOnIconClick: `**Overview**<br/>
The Trigger On Icon Click is an event handler that executes actions when the user clicks on the "end icon" of a Tree Column. It allows for interaction logic specifically tied to auxiliary icons within tree structures.

**When to Use**<br/>
Use this trigger to perform actions when the user clicks on end icon of Tree Column. It is useful for:
* **Secondary Actions:** Triggering context menus or specific operations distinct from selecting the row itself.
* **Navigation:** jumping to related details associated with that specific tree node.`,

  TriggerOnTreeColumnExpand: `**Overview**<br/>
The Trigger On Tree Column Expand is an event handler that executes actions when a user expands a row within a Tree Column. This captures the state change from collapsed to expanded.

**When to Use**<br/>
Use this trigger to perform actions when user expanded any tree column row. It is useful for:
* **Lazy Loading:** Fetching child nodes or related data only when the user explicitly requests to see them by expanding the row.
* **UI Tracking:** Logging user engagement with specific branches of a data tree.`,

  TriggerOnAggregateClick: `**Overview**<br/>
The Trigger On Aggregate Click is an event handler designed for Pivot Tables. It executes actions when a user clicks on a calculated aggregate value cell.

**When to Use**<br/>
Use this trigger to perform actions when the user clicks on an aggregate value on the Pivot Table. It is typically used for:
* **Drill-Down:** Navigating to a detailed view showing the individual records that make up the clicked aggregate sum or count.
* **Analysis:** Opening a chart or report specific to the selected metric.`,

  TriggerAfterLoad: `**Overview**<br/>
The Trigger After Load is an event handler that executes immediately after a Cloud Data Store load operation has been successfully completed. It signifies that data has been retrieved from the server and is now available in the store.

**When to Use**<br/>
Use this trigger to perform actions after a Cloud Data Store load is successfully executed. It is useful for:
* **Post-Load Processing:** Calculating derived values or summaries based on the newly loaded dataset.
* **UI Synchronization:** Hiding loading spinners or enabling buttons that require data presence.`,

  TriggerAfterQuery: `**Overview**<br/>
The Trigger After Query is an event handler that executes actions immediately after a Data Store query operation is successfully executed.

**When to Use**<br/>
Use this trigger to perform actions after a Data Store query is successfully executed. It is commonly used for:
* **Feedback:** Confirming to the user that search results have been updated.
* **Chained Operations:** Initiating secondary queries that depend on the results of the first.`,

  TriggerAfterSave: `**Overview**<br/>
The Trigger After Save is an event handler that executes actions immediately after a Data Store has successfully saved its changes to the server.

**When to Use**<br/>
Use this trigger to perform actions after a Data Store is successfully saved. It is primarily used for:
* **Success Notification:** Displaying a "Saved Successfully" toast message.
* **Navigation:** Redirecting the user to a list view or a different page after a form submission.
* **Resetting State:** Clearing form flags or temporary variables used during editing.`,

  TriggerBeforeCreate: `**Overview**<br/>
The Trigger Before Create is an event handler that executes actions before a new row is officially created in a Data Store. It provides a hook to intercept the creation process and inject default values.

**When to Use**<br/>
Use this trigger to perform actions before creating a new row in a Data Store. It is specifically used to:
* **Populate Defaults:** Set initial values for the new record using the ActionSet action.
* **Access Partial Data:** Use the expression \`$._.new\` to access the partial row data being used to create the new entry.`,

  TriggerBeforeLoad: `**Overview**<br/>
The Trigger Before Load is an event handler that executes actions before a Cloud Data Store issues a load request to the server. It allows developers to modify the request parameters dynamically.

**When to Use**<br/>
Use this trigger to perform actions before a Cloud Data Store load is issued. It is designed to:
* **Set Default Filters:** Enforce filters that must be present in every load operation (e.g., filtering by the current user's ID).
* **Modify Request:** Access and modify the generated request object using the expression \`$._.request\`.`,

  TriggerBeforeQuery: `**Overview**<br/>
The Trigger Before Query is an event handler that executes actions immediately before a Data Store issues a query to the server. It acts as an interceptor, allowing developers to dynamically modify query parameters or apply global filters before the data retrieval process begins.

**When to Use**<br/>
Use this trigger to perform actions before a Data Store query is issued. It is critical for:
* **Contextual Filtering:** Setting default filters that must be present in every query, such as restricting data based on user roles or current date ranges.
* **Request Manipulation:** Accessing the generated request object using the expression \`$._.request\` to inspect or modify parameters programmatically.
* **Note:** You may have to refresh the browser to see the changes in the query.`,

  TriggerBeforeSave: `**Overview**<br/>
The Trigger Before Save is an event handler that executes actions immediately before a Data Store commits changes to the server. This is the primary hook for client-side logic that must pass before a save operation is permitted to proceed.

**When to Use**<br/>
Use this trigger to perform actions before a Data Store is saved. It is essential for:
* **Validation:** Performing client-side validations to ensure data integrity before issuing the save request.
* **Pre-Save Formatting:** Cleaning or formatting data fields (e.g., trimming whitespace) prior to submission.
* **User Confirmation:** Interrupting the save flow to ask for final user confirmation using dialog actions.`,

  TriggerOnBlur: `**Overview**<br/>
The Trigger On Blur is an event handler that executes actions when a user "tabs out" or loses focus from an input field. It captures the moment user interaction with a specific field concludes.

**When to Use**<br/>
Use this trigger to perform actions when the user tabs out of an input field. Key behaviors include:
* **Interaction completion:** Firing irrespective of whether the value has changed or not, making it distinct from change events.
* **Validation:** triggering immediate field-level validation messages as soon as the user leaves the field.
* **Note:** Use the Trigger On Value Change trigger if you only want to perform actions when the value has actually been modified.`,

  TriggerOnCellClick: `**Overview**<br/>
The Trigger On Cell Click is an event handler designed for Data Grid components. It executes actions specifically when a user clicks on an individual cell within the grid.

**When to Use**<br/>
Use this trigger to perform actions when the user click on a Data Grid cell. It is useful for:
* **Cell-Specific Interaction:** Handling logic that depends on the specific column or value clicked, rather than the row as a whole.
* **Inline Editing Modes:** Activating custom editors or modal views related to the specific data point selected.`,

  TriggerOnChange: `**Overview**<br/>
The Trigger On Change is an event handler that executes actions whenever the content of an input field is modified by the user. It provides real-time feedback capabilities as data is being entered.

**When to Use**<br/>
Use this trigger to perform actions when the user changes the content of an input field. It is typically used for:
* **Live Updates:** recalculating dependent fields or totals immediately as the user types.
* **Input Masking:** Enforcing specific character patterns or formatting rules dynamically.`,

  TriggerOnClick: `**Overview**<br/>
The Trigger On Click is a fundamental event handler that executes actions when a user performs a mouse click on a UI Component. It is the standard interaction trigger for buttons and clickable elements.

**When to Use**<br/>
Use this trigger to perform actions when the user performs a mouse click on the UI Component e.g. Button. It is the primary trigger for:
* **Action Initiation:** Submitting forms, opening dialogs, or navigating between pages.
* **State Toggling:** Switching UI modes or visibility states upon user interaction.`,

  TriggerOnComment: `**Overview**<br/>
The Trigger On Comment is an event handler that executes actions when a user posts a comment in the comments panel component. It provides access to rich metadata about the comment event, enabling complex social or notification workflows.

**When to Use**<br/>
Use this trigger to perform actions when the user comments in the comments panel component. The comment information is available in the \`$._.comment\` object, which includes:
* **$._.comment.commentText:** The actual text content of the comment.
* **$._.comment.commentedBy:** The username of the user who posted the comment.
* **$._.comment.commentUid:** The unique identifier for the specific comment.
* **$._.comment.mentions:** An array of unique usernames mentioned within the comment.
* **Example Usage:** You can access the comment information using expressions like: \`ActionShowMessage: \${$._.comment.commentedBy} said \${$._.comment.commentText}.\`.`,

  TriggerOnCreate: `**Overview**<br/>
The Trigger On Create is an event handler designed to execute actions specifically when a create operation occurs. This trigger allows developers to define logic that runs during the creation lifecycle of a component or record.

** When to Use**<br/>
Use this trigger to perform actions on create. It is typically used for:
* **Initialization Logic:** Executing specific workflows required immediately when a new instance or record is created.
* **Default Setup:** Establishing initial states or values that must be present right upon creation.`,

  TriggerOnInitialize: `**Overview**<br/>
The Trigger On Initialize is an event handler that executes actions immediately after the initialization of a component or page is complete. It serves as a lifecycle hook to run logic once the basic setup of the element is finished but before user interaction begins.

**When to Use**<br/>
Use this trigger to perform actions after initialization is complete. It is commonly used for:
* **Setup Logic:** Establishing initial states, fetching required reference data, or configuring UI elements dynamically upon load.
* **Default Values:** Setting specific values that depend on runtime context rather than static configuration.
* **Environment Checks:** Verifying user permissions or device capabilities before rendering the full UI.`,

  TriggerOnDelete: `**Overview**<br/>
The Trigger On Delete is an event handler specifically designed for Chip components. It executes actions immediately before a Chip is removed by the user, providing an opportunity to intercept or handle the removal intent.

**When to Use**<br/>
Use this trigger to perform actions before a Chip is being removed by the user. It is useful for:
* **Validation:** Checking if the chip can be removed or if it is mandatory.
* **State Updates:** Updating underlying data models to reflect the removal of the tag or entity represented by the chip.
* **Confirmation:** Intercepting the removal to ask the user if they are sure they want to remove the item.`,

  TriggerOnDoubleClick: `**Overview**<br/>
The Trigger On Double Click is an event handler that executes actions when a user performs a mouse double-click on a UI component. It is distinct from single-click interactions and is often used for "open" or "edit" operations.

**When to Use**<br/>
Use this trigger to perform actions when the user performs a mouse doubleclick on the applicable UI Component. It is typically used for:
* **Drill-Down:** Opening a detailed view or modal for an item (e.g., in a list or card view).
* **Mode Switching:** Toggling between "view" and "edit" modes for a specific element.
* **Quick Actions:** Executing a primary action that is distinct from selection (single click).`,

  TriggerOnDrop: `**Overview**<br/>
The Trigger On Drop is an event handler designed for Drag & Drop workflows. It executes actions specifically when a user drops a draggable item onto a valid Drop Target component.

**When to Use**<br/>
Use this trigger to perform actions when the user performs a drop in a Drag & Drop event on the Drop Target component. It is essential for:
* **Data Transfer:** Processing the data carried by the dragged item and applying it to the drop target.
* **Reordering:** Handling logic when items are rearranged within a list or grid via drag and drop.
* **Status Updates:** Moving an item from one status column to another (e.g., in a Kanban board).`,

  TriggerOnDateChange: `**Overview**<br/>
The Trigger On Date Change is an event handler designed for Calendar components. It executes actions specifically when the user navigates to or selects a different month/period in the calendar view.

**When to Use**<br/>
Use this trigger to perform actions when the user selects the month on the calendar. It is primarily used for:
* **Lazy Loading:** Fetching events or data specific to the newly selected month/period rather than loading all history at once.
* **UI Synchronization:** Updating headers, summaries, or related side-panels to reflect the currently viewed date range.`,

  TriggerOnEventChange: `**Overview**<br/>
The Trigger On Event Change is an event handler for Calendar components that executes actions when an existing event is modified via direct user interaction, such as dragging or resizing.

**When to Use**<br/>
Use this trigger to perform actions when the user updates a calendar event by dragging or resizing an event. It is critical for:
* **Rescheduling:** Updating the start or end times of an event in the backend when the user drags it to a new slot.
* **Duration Adjustment:** Capturing the new duration when a user resizes the event block.
* **Conflict Checking:** validating that the new time slot does not overlap with other locked events.`,

  TriggerOnEventClick: `**Overview**<br/>
The Trigger On Event Click is an event handler for Calendar components that executes actions when a user clicks on a specific event entry.

**When to Use**<br/>
Use this trigger to perform actions when the user clicks on a calendar event. It is typically used for:
* **Details View:** Opening a popover or modal displaying full details of the clicked event.
* **Selection:** Highlighting the event or making it the "active" record for other operations.
* **Contextual Actions:** Showing a specific set of actions (edit, delete) relevant to the clicked event.`,

  TriggerOnEventCreate: `**Overview**<br/>
The Trigger On Event Create is an event handler for Calendar components that executes actions when a user initiates the creation of a new event, typically by clicking on an empty time slot.

**When to Use**<br/>
Use this trigger to perform actions when the user creates a calendar event by clicking on an empty slot on the calendar. It is used to:
* **New Event Workflow:** Opening a "New Event" dialog pre-filled with the date and time corresponding to the clicked slot.
* **Quick Add:** Immediately creating a placeholder event at the selected time.
* **Reservation:** Locking a time slot temporarily while the user fills out details.`,

  TriggerOnFocus: `**Overview**<br/>
The Trigger On Focus is an event handler that executes actions when a user tabs into or clicks onto an input field, giving it focus.

**When to Use**<br/>
Use this trigger to perform actions when the user tabs into an input field. It is useful for:
* **Contextual Help:** Displaying tooltips or helper text relevant to the field currently being edited.
* **Formatting:** Clearing placeholder text or preparing the field for specific input formats (e.g., unmasking a password).
* **Validation Clearing:** Removing previous error messages as soon as the user attempts to correct the field.`,
  TriggerOnKeyDown: `**Overview**<br/>
The Trigger On Key Down is an event handler that executes actions whenever a user presses a keyboard key while an input field is focused. This provides low-level control over keyboard interactions within forms.

**When to Use**<br/>
Use this trigger to perform actions when the user presses any keyboard key on an input field. It is particularly useful for:
* **Hotkeys:** Detecting specific key combinations (like Ctrl+Enter) to trigger shortcuts.
* **Input Restriction:** Preventing the entry of invalid characters (e.g., non-numeric keys in a phone field) by intercepting the event.
* **Navigation:** Customizing tab order or cursor movement within a complex widget.`,

  TriggerOnMouseEnter: `**Overview**<br/>
The Trigger On Mouse Enter is an event handler that executes actions when the user's cursor moves over the boundaries of a component. It is primarily used for hover-based visual effects or contextual information display.

**When to Use**<br/>
Use this trigger to perform actions when the mouse enters the component. It is useful for:
* **Visual Feedback:** Changing the background color or adding a shadow to highlight the active element.
* **Tooltip Display:** Showing a custom floating panel or help text that explains the component's function.
* **Pre-fetching:** Initiating a data load (like a user profile) the moment the mouse hovers over an avatar, anticipating a click.`,

  TriggerOnMouseLeave: `**Overview**<br/>
The Trigger On Mouse Leave is an event handler that executes actions when the user's cursor moves outside the boundaries of a component. It acts as the cleanup or reset counterpart to Mouse Enter.

**When to Use**<br/>
Use this trigger to perform actions when the mouse leaves the component. It is commonly used for:
* **Resetting Styles:** Reverting the component's appearance to its default state after a hover effect.
* **Hiding Overlays:** Closing tooltips, flyout menus, or preview panels that were opened on hover.
* **Validation:** Triggering a "soft" validation check when the user moves away from an area of interest.`,

  TriggerOnPageActive: `**Overview**<br/>
The Trigger On Page Active is a navigation event handler that executes actions immediately when a specific page becomes the active view in the application. It acts as a page-level initialization hook.

**When to Use**<br/>
Use this trigger to perform actions when the user navigates to a page. It is essential for:
* **Data Refresh:** Reloading grid data or dashboard metrics to ensure the user sees the latest information every time they visit.
* **Analytics Tracking:** Logging a "Page View" event to an analytics service.
* **State Restoration:** Resetting scroll positions or clearing temporary search filters from previous sessions.`,

  TriggerOnPageExit: `**Overview**<br/>
The Trigger On Page Exit is a navigation event handler that executes actions just before the user navigates away from the current page. It serves as a cleanup hook for page-level logic.

**When to Use**<br/>
Use this trigger to perform actions when the user navigates out of a page. It is typically used for:
* **Unsaved Changes Check:** Warning the user if they are leaving a form with dirty/unsaved data.
* **Resource Cleanup:** Stopping active timers, intervals, or subscriptions that are specific to that page.
* **Saving State:** Persisting temporary UI preferences (like specific filter settings) to the session store for the next visit.`,

  TriggerOnPopoverClose: `**Overview**<br/>
The Trigger On Popover Close is an event handler that executes actions when a popover component (a small overlay) is dismissed or hidden.

**When to Use**<br/>
Use this trigger to perform actions when a popover is closed. It is useful for:
* **State Sync:** Updating the parent component to reflect that the detail view is no longer visible.
* **Data Refresh:** Refreshing the main view if actions taken inside the popover modified the underlying data.
* **Cleanup:** Resetting forms or temporary variables used inside the popover.`,

  TriggerOnPopoverOpen: `**Overview**<br/>
The Trigger On Popover Open is an event handler that executes actions immediately when a popover component is triggered to appear.

**When to Use**<br/>
Use this trigger to perform actions when a popover is opened. It is primarily used for:
* **Lazy Loading:** Fetching the detailed content for the popover (e.g., user details card) only at the moment it is opened.
* **Initialization:** Setting default focus to the first input field inside the popover.
* **Tracking:** Logging which items users are inspecting via popovers.`,

  TriggerOnPopupClose: `**Overview**<br/>
The Trigger On Popup Close is an event handler designed for modal dialogs and popups. It executes actions when the popup is dismissed, either via a close button, clicking the backdrop, or a programmatic close.

**When to Use**<br/>
Use this trigger to perform actions when a popup is closed. It is critical for:
* **Post-Process Logic:** Running workflows that depend on the result of the popup (e.g., refreshing a grid after a "Add New Record" modal is closed).
* **Resetting Context:** Clearing the specific record ID or data object that was being edited in the popup.`,

  TriggerOnPopupOpen: `**Overview**<br/>
The Trigger On Popup Open is an event handler designed for modal dialogs and popups. It executes actions immediately when the popup is displayed to the user.

**When to Use**<br/>
Use this trigger to perform actions when a popup is opened. It is commonly used for:
* **Data Preparation:** Fetching specific record details to populate the form fields inside the popup.
* **Context Setup:** Setting global or session variables that define the mode of the popup (e.g., "Edit" vs "Create" mode).`,

  TriggerOnResponse: `**Overview**<br/>
The Trigger On Response is a specialized event handler that executes actions when a network response (typically from an API call or webhook) is received. It allows logic to run based on the raw response data.

**When to Use**<br/>
Use this trigger to perform actions when a response is received. The response object is available in the \`$._.response\` object. It is used for:
* **Response Inspection:** Analyzing headers or status codes to determine the success or failure of a complex request.
* **Data Extraction:** parsing specific fields from the \`$._.response\` body that might not be automatically mapped to a store.
* **Error Handling:** Triggering specific alerts if the response contains custom error codes.`,

  TriggerOnRowClick: `**Overview**<br/>
The Trigger On Row Click is an event handler for Data Grids that executes actions when a user clicks anywhere on a table row. This is the primary interaction for selecting records.

**When to Use**<br/>
Use this trigger to perform actions when the user performs a mouse click on a Data Grid row. It is essential for:
* **Master-Detail Selection:** Updating a side panel or secondary grid to show details relevant to the clicked row.
* **Navigation:** redirecting the user to a dedicated "Details Page" for the selected record.
* **Selection State:** Toggling a "selected" visual state for the row.`,

  TriggerOnRowDoubleClick: `**Overview**<br/>
The Trigger On Row Double Click is an event handler for Data Grids that executes actions when a user double-clicks a table row. It distinguishes a strong intent (like "Open" or "Edit") from a simple selection.

**When to Use**<br/>
Use this trigger to perform actions when the user performs a mouse doubleclick on a Data Grid row. It is typically used for:
* **Edit Mode:** Immediately opening the record in an editor modal or page.
* **Drill-Down:** Navigating to a deeper hierarchy or related entity view.
* **Primary Action:** Executing the default action for that row (e.g., "Play" for a media item).`,

  TriggerOnSearch: `**Overview**<br/>
The Trigger On Search is an event handler designed for the Smart Search component. It executes actions specifically after the user commits a search operation (e.g., by pressing Enter or clicking the search icon).

**When to Use**<br/>
Use this trigger to perform actions after the user performs a search on the smart search component. It is useful for:
* **Custom Filtering:** Taking the search term and applying complex filtering logic to multiple data stores simultaneously.
* **Analytics:** Logging the search terms used by users to understand what they are looking for.
* **UI Updates:** Switching the view from a "Dashboard" to a "Search Results" layout.`,

  TriggerOnSubmit: `**Overview**<br/>
The Trigger On Submit is an event handler specifically designed for the Signature Pad component. It executes actions when the user clicks the confirmed "Submit" or "Done" button associated with the signature capture.

**When to Use**<br/>
Use this trigger to perform actions on click of submit button of signature pad. It is used for:
* **Capture & Save:** Taking the captured signature image data and uploading it to a file store or attaching it to a record.
* **Validation:** Ensuring the signature is not empty before allowing the user to proceed.
* **Workflow Progression:** Automatically moving to the next step of a contract or approval wizard once the signature is provided.`,

  TriggerOnSubscriptionEvent: `Use this trigger to perform actions when a subscribed business event is received. Refer to \`Event Subscription\` component for more info on business events.
The event object provided which publishing the business event can be accessed using \`$._.event.data\`

e.g. when you publish an event using the below code on the server side scripts or cloud functions
\`\`\`javascript
await db.publish(context, contextId, {someValue: 100});
\`\`\`

The variable \`someValue\` passed above can be accessing using the below expression in any action under this trigger.
\`\`\`javascript
$._.event.data.someValue
\`\`\``,
  TriggerOnTabActive: `**Overview**<br/>
The Trigger On Tab Active is an event handler that executes actions when a tab within a Tab Panel component is activated. This can occur either through direct user interaction (clicking the tab header) or programmatically via logic.

**When to Use**<br/>
Use this trigger to perform actions when a tab in a tab panel is activated. It is typically used for:
* **Lazy Loading:** Fetching data or loading heavy components only when the specific tab is opened, improving initial page load performance.
* **Analytics Tracking:** Logging user navigation to specific tabs within a dashboard.
* **Context Refresh:** Resetting or refreshing data specific to the tab's context whenever it comes into view.
* **Focus Management:** Programmatically setting focus to a specific input field inside the tab once it becomes active.`,

  TriggerOnTabExit: `**Overview**<br/>
The Trigger On Tab Exit is an event handler that executes actions when a user leaves a specific tab within a Tab Panel. This event fires just before the active view switches to a different tab or component.

**When to Use**<br/>
Use this trigger to perform actions when a tab in a tab panel is exited either programmatically or by the user action. It is essential for:
* **Validation:** Checking if the data in the current tab is valid before allowing the user to switch away.
* **Auto-Save:** Automatically saving draft data entered in the tab to prevent loss during navigation.
* **Cleanup:** Stopping active processes, such as media playback or data polling, that are only relevant when the tab is visible.`,

  TriggerOnInterval: `**Overview**<br/>
The Trigger On Interval is an event handler designed to execute actions repeatedly at a specified time interval. It allows for the creation of periodic background tasks or cyclical UI updates.

**When to Use**<br/>
Use this trigger to perform actions on a recurring schedule. It is commonly used for:
* **Data Polling:** Automatically refreshing data from the server every few seconds to show real-time updates.
* **Carousels/Slideshows:** Automatically advancing to the next item in a list or view at set intervals.
* **Live Status:** Updating "time since" indicators or other time-sensitive UI elements.`,

  TriggerOnMediumScreen: `**Overview**<br/>
The Trigger On Medium Screen is a responsive event handler that executes actions when the application window's width matches the "Medium" breakpoint definition. It fires both when the screen is resized to this width and if the screen is already medium-sized upon activation.

**When to Use**<br/>
Use this trigger to perform actions when the screen size changes to medium or it is medium when activated. It is useful for:
* **Layout Adaptation:** Adjusting grid columns, hiding sidebars, or resizing components to fit tablet-sized viewports.
* **Conditional Logic:** Enabling or disabling specific features that are only optimized for medium screens.
* **Visual Feedback:** Displaying specific hints or navigation elements suitable for the available screen real estate.`,

  TriggerOnMention: `**Overview**<br/>
Use this trigger to perform actions when a user is mentioned in the comments. The mentioned user can be accessed using the expression \`$._.comment.mention\` and the comment text can be accessed using the expression \`$._.comment.comment\`.

\`\`\`alert
Note: This trigger will be executed once for each user mentioned in the comment. If a user is mentioned multiple times in the comment, this trigger will be executed only once for that user. If you want to send a single notification to all the mentioned users, you can use the \`Trigger on Comment\`.
\`\`\`

\`\`\`javascript
// Access the comment information as below using expression
\`\${$._.comment.commentedBy} \${$._.comment.commentText} \${$._.comment.commentUid} \${$._.comment.mention}\`
\`\`\``,
  TriggerOnLargeScreen: `**Overview**<br/>
The Trigger On Large Screen is a responsive event handler that executes actions when the application window's width matches the "Large" breakpoint definition. It validates this condition both when the screen is resized and immediately upon activation if the screen is already large.

** When to Use**<br/>
Use this trigger to perform actions when the screen size changes to large or it is large when activated. It is typically used for:
* **Desktop Optimization:** Expanding layouts to utilize available width, such as showing multiple columns in a data grid.
* **Navigation Visibility:** Un-collapsing sidebars or navigation drawers that are hidden on smaller devices.
* **High-Fidelity Content:** Loading higher resolution assets or complex visualizations suitable for larger viewing areas.`,

  TriggerOnSmallScreen: `**Overview**<br/>
The Trigger On Small Screen is a responsive event handler that executes actions when the application window's width matches the "Small" breakpoint definition. It fires during resize events entering this range or on initialization if the device fits this category.

** When to Use**<br/>
Use this trigger to perform actions when the screen size changes to small or it is small when activated. It is essential for:
* **Mobile Adaptation:** Switching to stacked layouts or list views instead of tables.
* **Space Management:** Hiding non-critical UI elements to declutter limited screen real estate.
* **Touch Optimizations:** Adjusting button sizes or spacing for touch interaction if the small screen implies a mobile device.`,

  TriggerOnExtraSmallScreen: `**Overview**<br/>
The Trigger On Extra Small Screen is a responsive event handler that executes actions when the application window's width matches the "Extra Small" breakpoint definition. This targets the smallest class of devices, typically mobile phones in portrait mode.

** When to Use**<br/>
Use this trigger to perform actions when the screen size changes to extra small or it is extra small when activated. It is primarily used for:
* **Critical Layout Shifts:** Moving distinct panels into tabs or separate pages to fit narrow widths.
* **Content Reduction:** Displaying only the most vital information and hiding secondary details behind "Show More" actions.
* **Menu Collapsing:** Converting horizontal menu bars into hamburger menus.`,

  TriggerOnExtraLargeScreen: `**Overview**<br/>
The Trigger On Extra Large Screen is a responsive event handler that executes actions when the application window's width matches the "Extra Large" breakpoint definition. It targets very wide displays or high-resolution monitors.

** When to Use**<br/>
Use this trigger to perform actions when the screen size changes to extra large or it is extra large when activated. It is useful for:
* **Dashboard Density:** Displaying the maximum amount of data or widgets side-by-side.
* **Whitespace Management:** Adjusting margins or container max-widths to prevent content from stretching too far.`,

  TriggerOnPortraitScreen: `**Overview**<br/>
The Trigger On Portrait Screen is an orientation-based event handler. It executes actions when the device orientation changes to portrait (height > width) or if it initializes in that state.

** When to Use**<br/>
Use this trigger to perform actions when the screen orientation changes to portrait or it is portrait when activated. It is commonly used for:
* **Vertical Layouts:** Arranging components vertically to suit the taller aspect ratio.
* **Media Adjustments:** Resizing images or video players that may have been full-width in landscape.`,

  TriggerOnLandscapeScreen: `**Overview**<br/>
The Trigger On Landscape Screen is an orientation-based event handler. It executes actions when the device orientation changes to landscape (width > height) or if it initializes in that state.

** When to Use**<br/>
Use this trigger to perform actions when the screen orientation changes to landscape or it is landscape when activated. It is typically used for:
* **Horizontal Layouts:** Placing related components side-by-side.
* **Full-Screen Views:** Maximizing charts, grids, or media to take advantage of the wider aspect ratio.`,

  TriggerOnValueChange: `**Overview**<br/>
The Trigger On Value Change is an event handler that executes actions specifically when an input field loses focus (blur) and its value has been modified since it gained focus. It combines the logic of "on blur" with a check for data modification.

**When to Use**<br/>
Use this trigger to perform actions on blur when the value of the input field changes. It is distinct from TriggerOnChange (which fires immediately while typing) and TriggerOnBlur (which fires on exit regardless of change). It is best for:
* **Expensive Validation:** Running server-side checks (like "username availability") only after the user has finished typing and changed the value.
* **Dependency Updates:** Updating other form fields or totals only when a user commits a change to a specific input.`,

  TriggerOnWindowSizeChange: `**Overview**<br/>
The Trigger On Window Size Change is a comprehensive event handler that executes actions on page load and subsequently whenever the browser window dimensions change. Unlike specific breakpoint triggers (e.g., Small, Large), this fires on any resize event.

**When to Use**<br/>
Use this trigger to perform actions on page load and everytime the window size is changed. It is critical for:
* **Dynamic Calculations:** Calculating specific pixel dimensions for canvas elements or custom UI components that rely on exact window size.
* **Granular Responsiveness:** Implementing custom responsive logic that falls between or outside standard breakpoints.
* **Debugging:** Logging screen dimensions during development to test responsive behaviors.`,
};

const getDefaultChildren = (type: TriggerItemType): ItemType[] => {
  if (type === 'TriggerBeforeQuery') {
    return ['ActionSetQueryFilter'];
  }
  if (type === 'TriggerBeforeCreate') {
    return ['ActionSet'];
  }
  return [];
};

const TriggersManager =
  <K extends TriggerItemType>(): TriggerManagerFn<K> =>
  (platform: Platform, type: K): TriggerManager<K> => {
    const mgr: TriggerManager<K> = {
      ...baseTriggerManager(platform, type),
      getDisplayText: (item) => item.props.disableCondition ?? '',
      isChildAllowed: (page, parent, childType, { appUid, pageId }) => {
        if (getManager(childType).managerType !== 'Action') {
          return false;
        }
        switch (parent.type) {
          case 'TriggerBeforeLoad': {
            return (
              [
                'ActionAbort',
                'ActionLogToConsole',
                'ActionSetSimpleQueryFilter',
                'ActionShowMessage',
              ] as ActionItemType[]
            ).includes(childType as ActionItemType);
          }
          case 'TriggerBeforeQuery': {
            if (
              (
                [
                  'ActionAbort',
                  'ActionCopyFilters',
                  'ActionLogToConsole',
                  'ActionSetAggregateAttribute',
                  'ActionSetDistinct',
                  'ActionSetFilterCombiner',
                  'ActionSetFilterCombiner',
                  'ActionSetGroupByAttribute',
                  'ActionSetProjection',
                  'ActionSetQueryFilter',
                  'ActionSetSimpleQueryFilter',
                  'ActionSetSort',
                  'ActionShowMessage',
                ] as ActionItemType[]
              ).includes(childType as ActionItemType)
            ) {
              return true;
            }

            if (
              (
                ['ActionQuery', 'ActionQueryChart'] as ActionItemType[]
              ).includes(childType as ActionItemType)
            ) {
              // allow execution of query inside TriggerBeforeQuery
              // if copy filters exists
              const copyFilters = platform.getChildrenOfTypeById(
                appUid,
                pageId,
                parent.itemId,
                'ActionCopyFilters',
              );
              return copyFilters.length > 0;
            }
            return false;
          }
          case 'TriggerBeforeCreate': {
            return (
              [
                'ActionAbort',
                'ActionLogToConsole',
                'ActionSet',
                'ActionShowMessage',
              ] as ActionItemType[]
            ).includes(childType as ActionItemType);
          }
          case 'TriggerBeforeSave': {
            return (
              [
                'ActionAbort',
                'ActionFocus',
                'ActionLogToConsole',
                'ActionLoopRecords',
                'ActionMatchRecord',
                'ActionShowMessage',
                'ActionUserConfirmation',
              ] as ActionItemType[]
            ).includes(childType as ActionItemType);
          }
          default:
        }
        return true;
      },
      defaultValue: triggerDefaultValue as Required<TriggerItemTypeRegistry[K]>,
      defaultChildren: getDefaultChildren(type),
      props: {
        triggerOnTab: {
          label: 'Trigger On Tab',
          type: 'boolean',
          helpText: 'Enable triggering when the Tab key is pressed.',
        },
        disableCondition: {
          label: 'Trigger Condition',
          type: 'expressionOnly',
          returnType: (item, context) => 'boolean',
          helpText:
            'This trigger will be executed only if this condition is either blank or returns true.',
        },
      } as PropertyDefinitions<
        Omit<TriggerItemTypeRegistry[K], keyof CommonProps>
      >,
      name: triggerNames[type],
      managerType: 'Trigger',
      canHaveChildren: () => true,
      isPropertyVisible(prop): boolean {
        switch (prop) {
          case 'triggerOnTab':
            return type === 'TriggerOnEnterKeyPress';
          default:
            return true;
        }
      },
      replaceableWith: (item) => {
        switch (item.type) {
          case 'TriggerOnComment':
          case 'TriggerOnMention':
            return ['TriggerOnComment', 'TriggerOnMention'];
          case 'TriggerOnMouseEnter':
          case 'TriggerOnMouseLeave':
            return ['TriggerOnMouseEnter', 'TriggerOnMouseLeave'];
          case 'TriggerOnDoubleClick':
            return ['TriggerOnClick', 'TriggerOnDoubleClick'];
          case 'TriggerBeforeQuery':
          case 'TriggerAfterQuery':
            return ['TriggerBeforeQuery', 'TriggerAfterQuery'];
          case 'TriggerBeforeLoad':
          case 'TriggerAfterLoad':
            return ['TriggerBeforeLoad', 'TriggerAfterLoad'];
          case 'TriggerBeforeCreate':
          case 'TriggerBeforeSave':
          case 'TriggerAfterSave':
            return [
              'TriggerBeforeCreate',
              'TriggerBeforeSave',
              'TriggerAfterSave',
            ];
          case 'TriggerOnEventChange':
          case 'TriggerOnEventClick':
          case 'TriggerOnEventCreate':
            return [
              'TriggerOnEventChange',
              'TriggerOnEventClick',
              'TriggerOnEventCreate',
            ];
          case 'TriggerOnRowClick':
          case 'TriggerOnRowDoubleClick':
            return ['TriggerOnRowClick', 'TriggerOnRowDoubleClick'];
          case 'TriggerOnPageActive':
          case 'TriggerOnPageExit':
            return ['TriggerOnPageActive', 'TriggerOnPageExit'];
          case 'TriggerOnPopupClose':
          case 'TriggerOnPopupOpen':
            return ['TriggerOnPopupClose', 'TriggerOnPopupOpen'];
          case 'TriggerOnPopoverClose':
          case 'TriggerOnPopoverOpen':
            return ['TriggerOnPopoverClose', 'TriggerOnPopoverOpen'];
          case 'TriggerOnBlur':
          case 'TriggerOnFocus':
          case 'TriggerOnKeyDown':
            return ['TriggerOnBlur', 'TriggerOnFocus', 'TriggerOnKeyDown'];
          case 'TriggerOnSmallScreen':
          case 'TriggerOnMediumScreen':
          case 'TriggerOnLargeScreen':
          case 'TriggerOnExtraSmallScreen':
          case 'TriggerOnExtraLargeScreen':
          case 'TriggerOnPortraitScreen':
          case 'TriggerOnLandscapeScreen':
          case 'TriggerOnWindowSizeChange':
            return [
              'TriggerOnSmallScreen',
              'TriggerOnMediumScreen',
              'TriggerOnLargeScreen',
              'TriggerOnExtraLargeScreen',
              'TriggerOnExtraSmallScreen',
              'TriggerOnPortraitScreen',
              'TriggerOnLandscapeScreen',
              'TriggerOnWindowSizeChange',
            ];
          case 'TriggerOnChange':
          case 'TriggerOnValueChange':
            return ['TriggerOnChange', 'TriggerOnValueChange'];
        }
        return [];
      },
      icon: triggerIcons[type] ?? { iconName: 'rocket' },
      analyze: analyzeTrigger,
      description: triggerDesc[type],
      helpRenders: helpRenders[type] ?? [],
    };
    return mgr;
  };

export default TriggersManager;
